import { useState, useMemo } from 'react';
import { Book } from '@/types/models';

interface UseBookFiltersProps {
    books: Book[];
    initialSearch?: string;
    initialSort?: string;
    initialAuthorId?: string | null;
    initialGenreId?: string | null;
}

interface UseBookFiltersReturn {
    // Filtered books
    filteredBooks: Book[];
    
    // Current filter values
    search: string;
    sort: string;
    authorId: string | null;
    genreId: string | null;
    
    // Filter handlers
    handleSearch: (search: string) => void;
    handleSort: (sort: string) => void;
    handleAuthorFilter: (authorId: string | null) => void;
    handleGenreFilter: (genreId: string | null) => void;
}

export function useBookFilters({
    books,
    initialSearch = '',
    initialSort = 'newest',
    initialAuthorId = null,
    initialGenreId = null
}: UseBookFiltersProps): UseBookFiltersReturn {
    // Local state for filters
    const [search, setSearch] = useState(initialSearch);
    const [sort, setSort] = useState(initialSort);
    const [authorId, setAuthorId] = useState<string | null>(initialAuthorId);
    const [genreId, setGenreId] = useState<string | null>(initialGenreId);

    // Filter and sort books
    const filteredBooks = useMemo(() => {
        let filtered = [...books];

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(book => {
                const authorName = typeof book.author === 'string' ? book.author : book.author?.name || '';
                return book.title.toLowerCase().includes(searchLower) ||
                    authorName.toLowerCase().includes(searchLower);
            });
        }

        // Apply author filter
        if (authorId) {
            filtered = filtered.filter(book => book.author_id === authorId);
        }

        // Apply genre filter
        if (genreId) {
            filtered = filtered.filter(book => 
                book.genres?.some(genre => genre.id === genreId)
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            switch (sort) {
                case 'oldest':
                    return new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime();
                case 'title_asc':
                    return a.title.localeCompare(b.title);
                case 'title_desc':
                    return b.title.localeCompare(a.title);
                case 'author_asc': {
                    const aAuthor = typeof a.author === 'string' ? a.author : a.author?.name || '';
                    const bAuthor = typeof b.author === 'string' ? b.author : b.author?.name || '';
                    return aAuthor.localeCompare(bAuthor);
                }
                case 'author_desc': {
                    const aAuthor = typeof a.author === 'string' ? a.author : a.author?.name || '';
                    const bAuthor = typeof b.author === 'string' ? b.author : b.author?.name || '';
                    return bAuthor.localeCompare(aAuthor);
                }
                case 'rating_high':
                    return (parseFloat(b.rating_value || '0') - parseFloat(a.rating_value || '0'));
                case 'rating_low':
                    return (parseFloat(a.rating_value || '0') - parseFloat(b.rating_value || '0'));
                default: // newest
                    return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
            }
        });

        return filtered;
    }, [books, search, sort, authorId, genreId]);

    const handleSearch = (newSearch: string) => {
        setSearch(newSearch);
    };

    const handleSort = (newSort: string) => {
        setSort(newSort);
    };

    const handleAuthorFilter = (newAuthorId: string | null) => {
        setAuthorId(newAuthorId);
    };

    const handleGenreFilter = (newGenreId: string | null) => {
        setGenreId(newGenreId);
    };

    return {
        filteredBooks,
        search,
        sort,
        authorId,
        genreId,
        handleSearch,
        handleSort,
        handleAuthorFilter,
        handleGenreFilter
    };
}